import {
  Box,
  CircularProgress,
  CircularProgressLabel,
  Flex,
  HStack,
  Icon,
  Text,
  VStack,
} from "@chakra-ui/react";
import { ShieldAlert } from "lucide-react";
import React from "react";

import { BasicTooltip, RouterLinkBox } from "../../../components";
import { DeepPick } from "../../../types";
import { formatDate } from "../../../utils/datetime";
import {
  AiInterviewEvaluationFragment,
  AiInterviewStatus,
} from "../../graphql";
import { QuestionScoreCounts } from "../AiRecording/Evaluation/QuestionScoreCounts";
import { RedactionAlert } from "../AiRecording/Evaluation/RedactionAlert";
import {
  calculateFinalScore,
  getFinalScoreColor,
  getScoreRange,
  shouldShowScores,
} from "../AiRecording/Evaluation/scoring";
import { AiInterviewerInvitationLinkButton } from "./AiInterviewerInvitationLinkButton";
import DisqualifiedBadge from "./DisqualifiedBadge";
import IncompleteBadge from "./IncompleteBadge";

type Evaluation = Pick<
  AiInterviewEvaluationFragment,
  "summary" | "points" | "maxPoints" | "disqualified"
> &
  DeepPick<AiInterviewEvaluationFragment, "questions", "points" | "qualifies">;

export type IntegritySignalSummary = {
  detectedCount: number;
};

/**
 * Candidate list item state determines how the item is displayed:
 * - waiting_to_start: Invitation sent but no interview started
 * - in_progress: Interview is currently in progress
 * - summary_pending: Interview finished, waiting for evaluation
 * - summary_failure: Interview finished but evaluation failed
 * - completed: Interview completed with evaluation
 */
export enum CandidateListItemState {
  WaitingToStart = "waiting_to_start",
  InProgress = "in_progress",
  SummaryPending = "summary_pending",
  SummaryFailure = "summary_failure",
  Completed = "completed",
}

export type AiInterviewerListInterviewItemType = {
  id: string;
  invitationId?: string;
  invitationLink?: string;
  isInvitationExpired?: boolean;
  callId?: string;
  canEdit?: boolean;
  callCreatedAt?: string;
  candidateName: string;
  positionTitle?: string | null;
  /** The name of the AI Interviewer / Screen for display in the subtitle */
  aiInterviewerName?: string;
  evaluation?: Evaluation | null;
  isIncomplete: boolean;
  evaluationDisabled?: boolean;
  hasRedactedQuestions: boolean;
  /** The state of this candidate in the interview process */
  state: CandidateListItemState;
  /** Interview status from backend (for detailed state handling) */
  interviewStatus?: AiInterviewStatus;
  /** Whether the interviewer has scorable questions - determines if scoring UI should be shown */
  hasScorableQuestions: boolean;
  /** Summary of detected integrity signals for this interview */
  integritySignals?: IntegritySignalSummary | null;
  /** Whether to show integrity signal UI (feature-gated) */
  showIntegritySignals?: boolean;
};

export const AiInterviewerListInterviewItem: React.FC<
  AiInterviewerListInterviewItemType
> = ({
  callId,
  canEdit,
  invitationId,
  invitationLink,
  isInvitationExpired,
  callCreatedAt,
  candidateName,
  positionTitle,
  aiInterviewerName,
  evaluation,
  isIncomplete,
  evaluationDisabled,
  hasRedactedQuestions,
  state,
  hasScorableQuestions,
  integritySignals,
  showIntegritySignals,
}) => {
  // Link to interview if there's a callId (any interview that has started)
  // Invites without a callId won't link, and incomplete/in-progress interviews will show empty state
  const canLinkToInterview = !!callId;
  const BoxElement = canLinkToInterview ? RouterLinkBox : Box;
  const linkProps = canLinkToInterview
    ? {
        to: `/interview/${callId}`,
      }
    : { to: "" };

  const showScores = evaluation && shouldShowScores(evaluation);
  const finalScore = showScores ? calculateFinalScore(evaluation) : 0;
  const isDisqualified = evaluation?.disqualified;

  const showCompletedState = evaluation && !isIncomplete;

  return (
    <BoxElement
      {...linkProps}
      data-testid={`candidate-list-item-${state}`}
      px="4"
      py="3"
      borderRadius="xl"
      borderWidth="1px"
      borderColor="gray.200"
      minW="0"
      w="100%"
      _hover={
        canLinkToInterview
          ? {
              bg: "blue.50",
              borderColor: "blue.500",
            }
          : undefined
      }
      cursor={canLinkToInterview ? "pointer" : "default"}
    >
      <Flex
        minH="8"
        gap={{ base: 2, lg: 4 }}
        align="center"
        justify="space-between"
        flexWrap="wrap"
      >
        <Flex align="center" gap="2" flexWrap="wrap">
          <Box fontSize="md" color="gray.800" fontWeight="semibold">
            {candidateName}
          </Box>
          {callCreatedAt && (
            <Box fontSize="sm" color="gray.600" ml="2">
              {formatDate(callCreatedAt, {
                year: "numeric",
                month: "short",
                day: "numeric",
              })}
            </Box>
          )}
          {isIncomplete && <IncompleteBadge ml="2" />}
          {isDisqualified && <DisqualifiedBadge ml="2" />}
          {showIntegritySignals &&
            integritySignals &&
            state === CandidateListItemState.Completed && (
              <IntegritySignalsBadge signals={integritySignals} />
            )}
        </Flex>
        {invitationLink && invitationId && (
          <AiInterviewerInvitationLinkButton
            invitationId={invitationId}
            invitationLink={invitationLink}
            isInvitationExpired={!!isInvitationExpired}
            canEdit={canEdit}
          />
        )}
      </Flex>
      {(positionTitle || aiInterviewerName) && (
        <Text color="gray.600" fontSize="sm">
          {positionTitle}
          {positionTitle && aiInterviewerName && " | "}
          {aiInterviewerName}
        </Text>
      )}
      {showCompletedState ? (
        <>
          {/* Mobile: Scoring above text */}
          {showScores && (
            <Flex
              mt="3"
              gap="4"
              align="start"
              display={{ base: "flex", md: "none" }}
              mb="4"
            >
              <CircularProgress
                value={finalScore}
                color={getFinalScoreColor(finalScore)}
                trackColor="gray.100"
                size="52px"
                capIsRound
                thickness="6px"
                flexShrink={0}
              >
                <CircularProgressLabel color={getFinalScoreColor(finalScore)}>
                  <Text color={getFinalScoreColor(finalScore)} fontSize="xl">
                    {finalScore}
                  </Text>
                </CircularProgressLabel>
              </CircularProgress>
              <VStack gap="1" flex="1">
                <Text
                  fontSize="xs"
                  bg="blackAlpha.50"
                  color="gray.600"
                  minWidth="100px"
                  textAlign="center"
                  borderRadius="6px"
                  p="1"
                >
                  Score:{" "}
                  <Text as="span" color="gray.800">
                    {getScoreRange(evaluation)}
                  </Text>
                </Text>
                <QuestionScoreCounts evaluation={evaluation} />
              </VStack>
            </Flex>
          )}
          {/* Desktop: Horizontal layout with scoring on right */}
          <HStack mt="3" gap="4" align="start" flexWrap="wrap" minW="0">
            {showScores && (
              <CircularProgress
                value={finalScore}
                color={getFinalScoreColor(finalScore)}
                trackColor="gray.100"
                size="52px"
                capIsRound
                thickness="6px"
                flexShrink={0}
                display={{ base: "none", md: "flex" }}
              >
                <CircularProgressLabel color={getFinalScoreColor(finalScore)}>
                  <Text color={getFinalScoreColor(finalScore)} fontSize="xl">
                    {finalScore}
                  </Text>
                </CircularProgressLabel>
              </CircularProgress>
            )}

            {hasRedactedQuestions ? (
              <VStack gap="2" align="start" flex="1" minW="0">
                <RedactionAlert mb={0} w="100%" />
                <Text fontSize="sm">{evaluation.summary}</Text>
              </VStack>
            ) : (
              <Text fontSize="sm" flex="1" minW="0">
                {evaluation.summary}
              </Text>
            )}
            {showScores && (
              <VStack gap="1" display={{ base: "none", md: "flex" }}>
                <Text
                  fontSize="xs"
                  bg="blackAlpha.50"
                  color="gray.600"
                  minWidth="100px"
                  textAlign="center"
                  borderRadius="6px"
                  p="1"
                >
                  Score:{" "}
                  <Text as="span" color="gray.800">
                    {getScoreRange(evaluation)}
                  </Text>
                </Text>
                <QuestionScoreCounts evaluation={evaluation} />
              </VStack>
            )}
          </HStack>
        </>
      ) : (
        <AiInterviewerListInterviewItemNotice
          state={state}
          summaryOnly={false}
          isIncomplete={isIncomplete}
          evaluationDisabled={evaluationDisabled}
          hasScorableQuestions={hasScorableQuestions}
        />
      )}
    </BoxElement>
  );
};

/** Get notice message and background color based on state */
const getNoticeConfig = (
  state: CandidateListItemState,
  isIncomplete: boolean,
  evaluationDisabled: boolean | undefined
): { notice: string; noticeBg: string } => {
  if (isIncomplete) {
    return {
      notice: "The interview has not been completed.",
      noticeBg: "gray.100",
    };
  }
  if (evaluationDisabled) {
    return {
      notice: "Evaluation has been disabled for this interview.",
      noticeBg: "gray.100",
    };
  }

  switch (state) {
    case CandidateListItemState.WaitingToStart:
      return {
        notice: "Invite sent – waiting to start.",
        noticeBg: "gray.100",
      };
    case CandidateListItemState.InProgress:
      return {
        notice: "Interview in progress.",
        noticeBg: "green.50",
      };
    case CandidateListItemState.SummaryPending:
      return {
        notice: "The interview just finished. A summary will appear shortly.",
        noticeBg: "gray.100",
      };
    case CandidateListItemState.SummaryFailure:
      return {
        notice: "There was an issue generating this candidate's summary.",
        noticeBg: "red.50",
      };
    default:
      return {
        notice: "This interview has not been evaluated yet.",
        noticeBg: "gray.100",
      };
  }
};

const AiInterviewerListInterviewItemNotice: React.FC<{
  state: CandidateListItemState;
  summaryOnly?: boolean;
  isIncomplete?: boolean;
  evaluationDisabled?: boolean;
  hasScorableQuestions: boolean;
}> = ({
  state,
  summaryOnly,
  isIncomplete,
  evaluationDisabled,
  hasScorableQuestions,
}) => {
  const { notice, noticeBg } = getNoticeConfig(
    state,
    isIncomplete ?? false,
    evaluationDisabled
  );

  return (
    <>
      {/* Mobile: CircularProgress and Score above notice */}
      {!summaryOnly && !evaluationDisabled && hasScorableQuestions && (
        <Flex
          mt="3"
          gap="4"
          align="start"
          display={{ base: "flex", md: "none" }}
          mb="4"
        >
          <EmptyScoreCircle />
          <EmptyScoreDetails flex="1" />
        </Flex>
      )}
      {/* Desktop: Horizontal layout */}
      <HStack w="100%" gap="2" mt="3">
        {!summaryOnly && !evaluationDisabled && hasScorableQuestions && (
          <EmptyScoreCircle display={{ base: "none", md: "flex" }} />
        )}
        <Box
          flex="1"
          fontSize="md"
          fontWeight="400"
          lineHeight="20px"
          bg={noticeBg}
          borderRadius="md"
          p="3"
        >
          {notice}
        </Box>
        {!summaryOnly && !evaluationDisabled && hasScorableQuestions && (
          <EmptyScoreDetails display={{ base: "none", md: "flex" }} />
        )}
      </HStack>
    </>
  );
};

const EmptyScoreCircle: React.FC<
  React.ComponentProps<typeof CircularProgress>
> = (props) => (
  <CircularProgress
    trackColor="gray.100"
    size="52px"
    capIsRound
    thickness="6px"
    flexShrink={0}
    {...props}
  >
    <CircularProgressLabel color="gray.400">
      <Text color="gray.400" fontSize="xl">
        —
      </Text>
    </CircularProgressLabel>
  </CircularProgress>
);

const IntegritySignalsBadge: React.FC<{
  signals: IntegritySignalSummary;
}> = ({ signals }) => {
  if (signals.detectedCount === 0) return null;
  const color = "orange";
  return (
    <BasicTooltip label="Review integrity signals">
      <Flex
        align="center"
        gap="1"
        bg={`${color}.50`}
        borderRadius="md"
        px="2"
        py="0.5"
        ml="2"
      >
        <Icon as={ShieldAlert} boxSize="3.5" color={`${color}.600`} />
        <Text
          fontSize="xs"
          fontWeight="semibold"
          color={`${color}.600`}
          whiteSpace="nowrap"
        >
          {signals.detectedCount}{" "}
          {signals.detectedCount !== 1 ? "SIGNALS" : "SIGNAL"}
        </Text>
      </Flex>
    </BasicTooltip>
  );
};

const EmptyScoreDetails: React.FC<React.ComponentProps<typeof VStack>> = (
  props
) => (
  <VStack gap="1" {...props}>
    <Text
      fontSize="xs"
      bg="blackAlpha.50"
      color="gray.600"
      minWidth="100px"
      textAlign="center"
      borderRadius="6px"
      p="1"
    >
      Score:{" "}
      <Text as="span" color="gray.800">
        —/—
      </Text>
    </Text>
    <HStack spacing="2">
      <VStack spacing="1">
        <Box w="12px" h="12px" borderRadius="full" bg="green.600" />
        <Text fontSize="xs" color="gray.600">
          _
        </Text>
      </VStack>
      <VStack spacing="1">
        <Box w="12px" h="12px" borderRadius="full" bg="green.400" />
        <Text fontSize="xs" color="gray.600">
          _
        </Text>
      </VStack>
      <VStack spacing="1">
        <Box w="12px" h="12px" borderRadius="full" bg="yellow.400" />
        <Text fontSize="xs" color="gray.600">
          _
        </Text>
      </VStack>
      <VStack spacing="1">
        <Box w="12px" h="12px" borderRadius="full" bg="red.400" />
        <Text fontSize="xs" color="gray.600">
          _
        </Text>
      </VStack>
    </HStack>
  </VStack>
);
